/**
 * Renders ArchiMate icon for palette preview
 * Matches the SVG icons rendered on canvas
 */

import type { ElementType } from '@/lib/archimate/types';

interface Props {
  type: ElementType;
  size?: number;
}

export function PaletteIcon({ type, size = 16 }: Props) {
  // Render based on element type - matches canvas rendering
  if (type.includes('actor')) {
    return (
      <g>
        <circle cx={size/2} cy={size*0.3} r={size*0.15} fill="none" stroke="currentColor" strokeWidth="1"/>
        <line x1={size/2} y1={size*0.45} x2={size/2} y2={size*0.7} stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.25} y1={size*0.55} x2={size*0.75} y2={size*0.55} stroke="currentColor" strokeWidth="1"/>
        <line x1={size/2} y1={size*0.7} x2={size*0.3} y2={size*0.95} stroke="currentColor" strokeWidth="1"/>
        <line x1={size/2} y1={size*0.7} x2={size*0.7} y2={size*0.95} stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('role')) {
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.35} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size/2} y1={size*0.15} x2={size/2} y2={size*0.85} stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('collaboration')) {
    return (
      <g>
        <circle cx={size*0.35} cy={size/2} r={size*0.25} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx={size*0.65} cy={size/2} r={size*0.25} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('interface')) {
    // Circle with dot (lollipop)
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.35} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx={size/2} cy={size/2} r={size*0.12} fill="currentColor"/>
      </g>
    );
  }

  if (type.includes('process')) {
    return (
      <path
        d={`M ${size*0.1} ${size/2} L ${size*0.7} ${size/2} M ${size*0.6} ${size*0.3} L ${size*0.7} ${size/2} L ${size*0.6} ${size*0.7}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    );
  }

  if (type.includes('function')) {
    return (
      <path
        d={`M ${size*0.2} ${size*0.7} L ${size/2} ${size*0.25} L ${size*0.8} ${size*0.7}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    );
  }

  if (type.includes('interaction')) {
    return (
      <path
        d={`M ${size*0.2} ${size*0.3} Q ${size/2} ${size*0.8} ${size*0.8} ${size*0.3}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    );
  }

  if (type.includes('component')) {
    return (
      <g>
        <rect x={size*0.25} y={size*0.15} width={size*0.5} height={size*0.25} fill="none" stroke="currentColor" strokeWidth="1"/>
        <rect x={size*0.25} y={size*0.45} width={size*0.5} height={size*0.25} fill="none" stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('service')) {
    // Circle with notch (service port)
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.35} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <rect x={size*0.48} y={size*0.15} width={size*0.18} height={size*0.2} fill="white" stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('event')) {
    // Circle with clock marks
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.35} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size/2} y1={size/2} x2={size/2} y2={size*0.25} stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size/2} y1={size/2} x2={size*0.65} y2={size*0.45} stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('goal')) {
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.35} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx={size/2} cy={size/2} r={size*0.15} fill="currentColor"/>
      </g>
    );
  }

  if (type.includes('capability')) {
    const r = size*0.35;
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${size/2 + r * Math.cos(angle)},${size/2 + r * Math.sin(angle)}`);
    }
    return <polygon points={points.join(' ')} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  // Add all remaining icons
  if (type.includes('node') || type.includes('resource')) {
    return <rect x={size*0.2} y={size*0.25} width={size*0.6} height={size*0.5} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('device')) {
    return (
      <g>
        <rect x={size*0.2} y={size*0.2} width={size*0.6} height={size*0.5} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <rect x={size*0.35} y={size*0.7} width={size*0.3} height={size*0.1} fill="none" stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('equipment')) {
    return (
      <g>
        <circle cx={size/2} cy={size/2} r={size*0.3} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx={size/2} cy={size/2} r={size*0.15} fill="none" stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('facility')) {
    return <path d={`M ${size*0.2} ${size*0.5} L ${size/2} ${size*0.2} L ${size*0.8} ${size*0.5} L ${size*0.8} ${size*0.8} L ${size*0.2} ${size*0.8} Z`} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('work-package')) {
    return (
      <g>
        <rect x={size*0.2} y={size*0.2} width={size*0.6} height={size*0.6} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.2} y1={size*0.4} x2={size*0.8} y2={size*0.4} stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('stakeholder')) {
    return (
      <g>
        <circle cx={size*0.35} cy={size*0.3} r={size*0.12} fill="none" stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.35} y1={size*0.42} x2={size*0.35} y2={size*0.65} stroke="currentColor" strokeWidth="1"/>
        <circle cx={size*0.65} cy={size*0.3} r={size*0.12} fill="none" stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.65} y1={size*0.42} x2={size*0.65} y2={size*0.65} stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('outcome')) {
    return <path d={`M ${size*0.2} ${size*0.5} L ${size*0.4} ${size*0.7} L ${size*0.8} ${size*0.3}`} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>;
  }

  if (type.includes('principle')) {
    return (
      <g>
        <line x1={size/2} y1={size*0.2} x2={size/2} y2={size*0.8} stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.2} y1={size*0.4} x2={size*0.8} y2={size*0.4} stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('requirement')) {
    return (
      <g>
        <rect x={size*0.25} y={size*0.2} width={size*0.5} height={size*0.6} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.35} y1={size*0.4} x2={size*0.65} y2={size*0.4} stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.35} y1={size*0.6} x2={size*0.65} y2={size*0.6} stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('constraint')) {
    return (
      <g>
        <rect x={size*0.3} y={size*0.5} width={size*0.4} height={size*0.3} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.35} ${size*0.5} L ${size*0.35} ${size*0.4} Q ${size*0.35} ${size*0.25} ${size/2} ${size*0.25} Q ${size*0.65} ${size*0.25} ${size*0.65} ${size*0.4} L ${size*0.65} ${size*0.5}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('driver')) {
    return <path d={`M ${size*0.55} ${size*0.2} L ${size*0.35} ${size*0.5} L ${size*0.5} ${size*0.5} L ${size*0.35} ${size*0.8} L ${size*0.65} ${size*0.45} L ${size*0.5} ${size*0.45} Z`} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('assessment')) {
    return (
      <g>
        <line x1={size*0.2} y1={size*0.7} x2={size*0.4} y2={size*0.5} stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.4} y1={size*0.5} x2={size*0.6} y2={size*0.6} stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.6} y1={size*0.6} x2={size*0.8} y2={size*0.3} stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('value')) {
    return <path d={`M ${size/2} ${size*0.2} L ${size*0.8} ${size/2} L ${size/2} ${size*0.8} L ${size*0.2} ${size/2} Z`} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('business-object') || type.includes('data-object')) {
    return <rect x={size*0.25} y={size*0.3} width={size*0.5} height={size*0.4} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('contract')) {
    return (
      <g>
        <rect x={size*0.25} y={size*0.2} width={size*0.5} height={size*0.6} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <line x1={size*0.3} y1={size*0.7} x2={size*0.7} y2={size*0.7} stroke="currentColor" strokeWidth="1" strokeDasharray="2,1"/>
      </g>
    );
  }

  if (type.includes('representation')) {
    return (
      <g>
        <rect x={size*0.25} y={size*0.2} width={size*0.5} height={size*0.5} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.25} ${size*0.73} Q ${size*0.375} ${size*0.8} ${size/2} ${size*0.73} Q ${size*0.625} ${size*0.66} ${size*0.75} ${size*0.73}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('product')) {
    return (
      <g>
        <path d={`M ${size*0.25} ${size*0.2} L ${size*0.65} ${size*0.2} L ${size*0.75} ${size*0.3} L ${size*0.75} ${size*0.8} L ${size*0.25} ${size*0.8} Z`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.65} ${size*0.2} L ${size*0.65} ${size*0.3} L ${size*0.75} ${size*0.3}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('plateau')) {
    return <line x1={size*0.2} y1={size/2} x2={size*0.8} y2={size/2} stroke="currentColor" strokeWidth="2"/>;
  }

  if (type.includes('gap')) {
    return <line x1={size*0.2} y1={size/2} x2={size*0.8} y2={size/2} stroke="currentColor" strokeWidth="1.5" strokeDasharray="2,2"/>;
  }

  if (type.includes('path')) {
    return (
      <g>
        <path d={`M ${size*0.1} ${size/2} L ${size*0.9} ${size/2}`} stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.2} ${size*0.35} L ${size*0.1} ${size/2} L ${size*0.2} ${size*0.65}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.8} ${size*0.35} L ${size*0.9} ${size/2} L ${size*0.8} ${size*0.65}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('network')) {
    return (
      <g>
        <line x1={size*0.2} y1={size*0.35} x2={size*0.8} y2={size*0.35} stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.2} y1={size*0.65} x2={size*0.8} y2={size*0.65} stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.35} y1={size*0.2} x2={size*0.35} y2={size*0.8} stroke="currentColor" strokeWidth="1"/>
        <line x1={size*0.65} y1={size*0.2} x2={size*0.65} y2={size*0.8} stroke="currentColor" strokeWidth="1"/>
      </g>
    );
  }

  if (type.includes('material')) {
    return <path d={`M ${size/2} ${size*0.2} L ${size*0.8} ${size*0.8} L ${size*0.2} ${size*0.8} Z`} fill="none" stroke="currentColor" strokeWidth="1.5"/>;
  }

  if (type.includes('location')) {
    return (
      <g>
        <circle cx={size/2} cy={size*0.4} r={size*0.2} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size/2} ${size*0.6} L ${size/2} ${size*0.85}`} stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  if (type.includes('meaning')) {
    return (
      <g>
        <circle cx={size*0.45} cy={size*0.4} r={size*0.3} fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <path d={`M ${size*0.4} ${size*0.65} L ${size*0.3} ${size*0.85} L ${size*0.5} ${size*0.7}`} fill="none" stroke="currentColor" strokeWidth="1.5"/>
      </g>
    );
  }

  // Default: no icon (passive elements like business-object, data-object, etc.)
  return null;
}
